import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { useMarkOrderNotesSeen, useOrderNotesDetail } from '../../features/order-notes/api';
import { useAddOrgOrderNote } from '../../features/org-orders/notesApi';
import './order-notes-detail.scss';

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export default function OrderNotesDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const markedOrderRef = useRef<string | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = useRef(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useOrderNotesDetail(orderId);

  const notes = useMemo(() => {
    return data?.pages.flatMap((p) => p.notes) ?? [];
  }, [data?.pages]);

  const markSeen = useMarkOrderNotesSeen();
  const addNote = useAddOrgOrderNote(orderId || '');

  const [body, setBody] = useState('');

  useEffect(() => {
    if (!orderId) return;
    markedOrderRef.current = null;
    hasAutoScrolledRef.current = false;
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    if (markedOrderRef.current === orderId) return;

    markedOrderRef.current = orderId;
    markSeen.mutate(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const order = data?.pages?.[0]?.order;

  const unseenCount = useMemo(() => {
    return notes.filter((n) => !n.seen).length;
  }, [notes]);

  const orderedNotes = useMemo(() => {
    return [...notes].reverse();
  }, [notes]);

  useEffect(() => {
    if (!threadRef.current) return;
    if (!orderedNotes.length) return;
    if (hasAutoScrolledRef.current) return;

    threadRef.current.scrollTop = threadRef.current.scrollHeight;
    hasAutoScrolledRef.current = true;
  }, [orderedNotes]);

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed || !orderId) return;

    try {
      await addNote.mutateAsync(trimmed);
      setBody('');

      requestAnimationFrame(() => {
        if (threadRef.current) {
          threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
      });
    } catch (e) {
      console.error('Failed to add note:', e);
    }
  };

  return (
    <div className="layout">
      <AppSidebar />

      <div className="card order-notes-detail-page">
        <div className="order-notes-detail-page__header">
          <div className="order-notes-detail-page__header-left">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => navigate('/order-notes')}
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <div>
              <h2 className="card__title">{order?.customerName || 'Order Notes'}</h2>
              <p className="card__subtitle">
                Load: {order?.loadNumber || '—'}
                {unseenCount > 0 ? ` • ${unseenCount} new` : ''}
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="order-notes-detail-empty">Loading notes…</div>
        ) : !order ? (
          <div className="order-notes-detail-empty">Order not found.</div>
        ) : (
          <>
            <div className="order-notes-thread-wrap">
              {hasNextPage && (
                <div className="load-more-divider load-more-divider--top">
                  <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                    {isFetchingNextPage ? 'Loading…' : 'Load older notes'}
                  </button>
                </div>
              )}

              <div className="order-notes-thread" ref={threadRef}>
                {orderedNotes.length === 0 ? (
                  <div className="order-notes-detail-empty">No notes yet for this order.</div>
                ) : (
                  orderedNotes.map((note) => {
                    const isCustomer = note.authorType === 'CUSTOMER';

                    return (
                      <div
                        key={note.id}
                        className={`order-note-message ${
                          isCustomer ? 'order-note-message--customer' : 'order-note-message--org'
                        } ${!note.seen ? 'order-note-message--unseen' : ''}`}
                      >
                        <div className="order-note-message__top">
                          <div className="order-note-message__author">
                            <strong>{note.authorName}</strong>
                            <span className="order-note-message__author-type">
                              {note.authorType}
                            </span>
                            {!note.seen && (
                              <span className="order-note-message__new-badge">New</span>
                            )}
                          </div>

                          <div className="order-note-message__time">
                            {formatDateTime(note.createdAt)}
                          </div>
                        </div>

                        <div className="order-note-message__body">{note.body}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="order-notes-composer">
              <label className="order-notes-composer__label">Send a note</label>

              <div className="order-notes-composer__row">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write a note for this order…"
                  rows={4}
                  className="order-notes-composer__textarea"
                />

                <button
                  type="button"
                  className="btn btn--primary order-notes-composer__send"
                  onClick={handleSubmit}
                  disabled={!body.trim() || addNote.isPending}
                  title={!body.trim() ? 'Enter a note first' : ''}
                >
                  <Send size={16} />
                  {addNote.isPending ? 'Sending…' : 'Send note'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
